import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardEmbedState } from 'src/app/features/company-portal/modules/settings/models/MetabaseModels';
import { AnalyticsService } from 'src/app/features/company-portal/modules/settings/services/metabase.service';

export type DashboardErrorType =
  | 'access_denied'
  | 'unavailable'
  | 'stale'
  | 'disconnected'
  | 'generic'
  | null;

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.css'],
  standalone: false,
})
export class AnalyticsDashboardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dashboardId!: string;
  @Input() useSecureEmbedding = true;

  embedConfig: DashboardEmbedState | null = null;
  safeEmbedUrl!: SafeResourceUrl;
  isLoading = true;
  iframeLoaded = false;
  errorType: DashboardErrorType = null;
  errorMessage = '';
  companyId: number = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private analyticsService: AnalyticsService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.companyId = this.getCompanyId();

    if (this.dashboardId) {
      this.loadDashboard();
      return;
    }

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const mappingId = Number(params['mappingId']);
      if (!Number.isFinite(mappingId) || mappingId <= 0) {
        this.errorType = 'generic';
        this.errorMessage = 'Dashboard not found or not configured.';
        this.isLoading = false;
        return;
      }

      this.loadDashboardFromMapping(mappingId);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardId'] && !changes['dashboardId'].firstChange) {
      this.resetState();
      this.loadDashboard();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCompanyId(): number {
    return Number(JSON.parse(localStorage.getItem('user') || '{}')?.companyid) || 0;
  }

  private resetState(): void {
    this.isLoading = true;
    this.iframeLoaded = false;
    this.errorType = null;
    this.errorMessage = '';
    this.embedConfig = null;
  }

  private loadDashboardFromMapping(mappingId: number): void {
    this.resetState();
    this.analyticsService.getDashboardMappings(this.companyId).subscribe({
      next: (mappings) => {
        const mapping = mappings.find((m) => m.id === mappingId);
        if (!mapping?.dashboardId) {
          this.errorType = 'generic';
          this.errorMessage = 'Dashboard not found or not configured.';
          this.isLoading = false;
          return;
        }

        this.dashboardId = mapping.dashboardId;
        this.loadDashboard();
      },
      error: () => {
        this.errorType = 'generic';
        this.errorMessage = 'Failed to load dashboard mapping. Please try again.';
        this.isLoading = false;
      },
    });
  }

  loadDashboard(): void {
    if (!this.dashboardId) {
      this.errorType = 'generic';
      this.errorMessage = 'No dashboard ID provided';
      this.isLoading = false;
      return;
    }

    this.analyticsService.getDashboardEmbedUrl(this.companyId, this.dashboardId).subscribe({
      next: (config) => {
        this.embedConfig = config;
        this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(config.embedUrl);
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        if (err.status === 403) {
          this.errorType = 'access_denied';
          this.errorMessage = 'You do not have permission to view this dashboard.';
        } else if (err.status === 503 || err.status === 502) {
          this.errorType = 'unavailable';
          this.errorMessage = 'The analytics service is temporarily unavailable.';
        } else if (err.status === 404) {
          // Could be stale or disconnected â€” check error body
          const detail: string = err.error?.detail || err.error?.message || '';
          if (
            detail.toLowerCase().includes('stale') ||
            detail.toLowerCase().includes('dashboard')
          ) {
            this.errorType = 'stale';
            this.errorMessage = 'This dashboard is no longer available in Metabase.';
          } else if (detail.toLowerCase().includes('disconnect')) {
            this.errorType = 'disconnected';
            this.errorMessage = 'The analytics integration is disconnected.';
          } else {
            this.errorType = 'stale';
            this.errorMessage = 'This dashboard could not be found.';
          }
        } else {
          this.errorType = 'generic';
          this.errorMessage = 'Failed to load dashboard. Please try again.';
        }
      },
    });
  }

  getEmbedUrl(): SafeResourceUrl {
    if (!this.embedConfig) return '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.embedConfig.embedUrl);
  }

  onIframeLoad(): void {
    this.iframeLoaded = true;
  }

  reload(): void {
    this.resetState();
    this.loadDashboard();
  }

  navigateToSettings(): void {
    this.router.navigate(['/company-portal/settings/integrations']);
  }

  get hasError(): boolean {
    return this.errorType !== null;
  }
}
